<template>
  <div
    class="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 hover:border-teal-300 dark:hover:border-teal-700 transition-all duration-200 px-3 py-2.5 sm:px-4 sm:py-3"
    :class="{
      'opacity-60': isPerformed && !isUpNext,
      'ring-2 ring-teal-500 ring-offset-2 border-teal-500': hasVoted && !isPerformed && !isUpNext,
      'bg-teal-50/50 dark:bg-teal-900/10': hasVoted && !isPerformed && !isUpNext,
      'ring-2 ring-amber-500/80 ring-offset-0 border-amber-500/50 bg-amber-50/50 dark:bg-amber-900/10':
        isUpNext,
      'shadow-sm hover:shadow-md': !isPerformed
    }"
  >
    <div class="songcard flex items-center justify-between gap-3">
      <!-- Left: title / author / genre / state badges -->
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-1.5">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-white truncate">
            {{ song.title }}
          </h3>
          <i
            v-if="isPerformed"
            class="pi pi-check-circle text-teal-600 dark:text-teal-400 text-xs"
            :title="
              hasVoted && isPerformed
                ? t('event.votedAndPerformedTooltip')
                : t('event.performedTooltip')
            "
          ></i>
        </div>
        <p v-if="song.author" class="text-gray-600 dark:text-gray-400 text-xs truncate">
          {{ song.author }}
        </p>
        <div class="flex items-center gap-1.5 mt-0.5 flex-wrap">
          <span
            v-for="category in categoryList"
            :key="category"
            class="inline-block px-1.5 py-0.5 text-[11px] bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded"
          >
            {{ category }}
          </span>
          <span
            v-if="isPerformed && hasVoted"
            class="inline-block px-1.5 py-0.5 text-[11px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded font-semibold"
          >
            {{ t("event.performedAndVoted") }}
          </span>
          <span
            v-else-if="isPerformed"
            class="inline-block px-1.5 py-0.5 text-[11px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded font-semibold"
          >
            {{ t("event.performed") }}
          </span>
        </div>
      </div>

      <!-- Right: compact vote count + action -->
      <div class="flex items-center gap-2 shrink-0">
        <!-- Up Next: show lock icon instead of votes -->
        <div v-if="isUpNext" class="flex items-center gap-2">
          <span
            class="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-md"
          >
            <i class="pi pi-lock text-xs"></i>
            {{ t("event.upNext") }}
          </span>
        </div>

        <template v-else-if="showVoteCount || showVoteButton">
          <div v-if="showVoteCount" class="text-right leading-tight">
            <div class="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
              {{ t("event.votes") }}
            </div>
            <div class="text-sm font-semibold text-gray-900 dark:text-white">
              {{ voteCount }}
            </div>
          </div>

          <button
            v-if="showVoteButton && (isVoting || (!hasVoted && !isPerformed))"
            :disabled="isVoting || !canVote"
            :aria-disabled="isVoteLocked || isVoting || !canVote"
            :class="{
              'pointer-events-none': isVoteLocked && !isVoting,
              'bg-teal-500/80 hover:bg-teal-500/80 text-white/90': isVoteLocked && !isVoting
            }"
            class="w-10 h-10 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center text-xs sm:text-sm font-medium shadow-sm hover:shadow-md disabled:shadow-none"
            @click="handleVote"
          >
            <i v-if="isVoting" class="pi pi-spin pi-spinner text-xs sm:text-sm"></i>
            <i v-else class="pi pi-thumbs-up-fill text-xs sm:text-sm"></i>
          </button>

          <!-- Voted, not yet played (icon only) -->
          <div
            v-else-if="hasVoted && !isPerformed"
            class="w-10 h-10 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 rounded-md flex items-center justify-center text-[11px]"
            :title="t('event.votedTooltip')"
          >
            <i class="pi pi-check text-xs"></i>
          </div>

          <!-- Played (with or without vote, icon only) -->
          <div
            v-else
            class="w-10 h-10 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-md flex items-center justify-center text-[11px] cursor-not-allowed"
            :title="t('event.performedTooltip')"
          >
            <i v-if="hasVoted" class="pi pi-check text-xs"></i>
            <i v-else class="pi pi-thumbs-up text-xs"></i>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { defineProps, defineEmits } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  song: {
    type: Object,
    required: true
  },
  eventId: {
    type: Number,
    required: true
  },
  voteCount: {
    type: Number,
    default: 0
  },
  isPerformed: {
    type: Boolean,
    default: false
  },
  isUpNext: {
    type: Boolean,
    default: false
  },
  canVote: {
    type: Boolean,
    default: true
  },
  hasVoted: {
    type: Boolean,
    default: false
  },
  isVoting: {
    type: Boolean,
    default: false
  },
  isVoteLocked: {
    type: Boolean,
    default: false
  },
  showVoteCount: {
    type: Boolean,
    default: true
  },
  showVoteButton: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(["vote"]);

const categoryList = computed(() => {
  if (!props.song) return [];
  if (Array.isArray(props.song.categories) && props.song.categories.length > 0) {
    return props.song.categories;
  }
  if (props.song.category) {
    return [props.song.category];
  }
  return [];
});

const handleVote = async () => {
  if (props.isVoting || props.isVoteLocked || props.isPerformed || !props.canVote || props.hasVoted)
    return;
  emit("vote", props.song.id);
};
</script>
